import React, { useRef, useState, useEffect } from "react";
import { aboutData, contactData, socialLinks } from "@/data/mock";
import { Instagram, Youtube, Mail, Flame } from "lucide-react";

const FlickrIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="7" cy="12" r="5.5" fill="#0063DC" />
    <circle cx="17" cy="12" r="5.5" fill="#FF0084" />
  </svg>
);

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="about" ref={sectionRef} className="bg-[#1A1A1A] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Text */}
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <h2 className="font-bebas text-4xl sm:text-5xl lg:text-7xl text-white tracking-[0.1em] mb-3">
              ABOUT ME
            </h2>
            <div className="font-inter text-xs text-white/40 uppercase tracking-[0.25em] leading-[2.5] mb-10">
              <p>Cinematic Travel Creator</p>
              <p>Drone Videographer</p>
              <p>Visual Storyteller</p>
            </div>
            <p className="font-inter text-white/60 leading-relaxed mb-4 text-[15px]">
              {aboutData.text}
            </p>
            <p className="font-inter text-white/60 leading-relaxed mb-10 text-[15px]">
              {aboutData.longText}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollTo("#contact")}
                className="px-7 py-3.5 border border-white/30 text-white font-inter text-xs uppercase tracking-[0.15em] hover:bg-white hover:text-[#1A1A1A] transition-all duration-300"
              >
                Let's create!
              </button>
            </div>
          </div>

          {/* Right: Image + Social */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="overflow-hidden mb-6">
              <img
                src={aboutData.image}
                alt="Ankit Mohanty - Travel Creator"
                className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover"
                style={{ filter: "saturate(0.7) contrast(1.1) brightness(0.92) sepia(0.15)" }}
                loading="lazy"
              />
            </div>
            <div className="flex gap-4 justify-end">
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/40 hover:text-white hover:border-white/60 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/40 hover:text-white hover:border-white/60 transition-all duration-300"
                aria-label="YouTube"
              >
                <Youtube size={16} />
              </a>
              <a
                href={socialLinks.flickr}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/40 hover:text-white hover:border-white/60 transition-all duration-300"
                aria-label="Flickr"
              >
                <FlickrIcon size={14} />
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AboutSection;
